import { getAgentV2ReferenceDisplayName } from '@/agent-v2/utils/agent-v2-reference-displays'
import { AgentV2UnavailableSource } from '@/agent-v2/references/AgentV2UnavailableSource'
import { AgentV2WebSourceIcon } from '@/agent-v2/references/AgentV2WebSourceIcon'
import { Link } from '@/components/link/Link'
import { AgentV2WebSourceTooltip } from '@/agent-v2/references/AgentV2WebSourceTooltip'
import Markdown, { type Components } from 'react-markdown'
import rehypeRaw from 'rehype-raw'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import rehypeWrap from 'rehype-wrap-all'
import remarkGfm from 'remark-gfm'
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode, type ComponentPropsWithoutRef } from 'react'
import * as Sentry from '@sentry/browser'
import rehypeTagNameChange from '@/util/rehype-custom-plugins/rehype-tag-swap'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'
import {
  kAgentV2CitationHrefPrefix,
  kAgentV2LegalCitationHrefPrefix,
  kAgentV2SourceUnavailableLabel,
  kAgentV2UnverifiedSourceLabel,
  labelAgentV2Citations,
  replaceAgentV2CitationTokens,
} from '@/agent-v2/references/agent-v2-citation-labels'
import { canOpenAgentV2Reference, getAgentV2ExternalReferenceUrl } from '@/agent-v2/references/agent-v2-reference-opening'
import { InlineTextButton } from '@/components/inline-text-button/InlineTextButton'
import { AgentV2CitationTooltip } from '../references/AgentV2CitationTooltip'
import { InlineTreatmentFlag } from '@/citator/components/InlineTreatmentFlag'
import { isWarningCaseStatus } from '@/citator/schema/case-status-schema'
import { CitatorSurface, mapCaseStatusToBadgeType } from '@/analytics/enums/citator.enums'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackCitatorBadgeImpression } from '@/analytics/utils/citator-analytics'

/** Return null to keep the ordinary link behavior. Citation links are handled internally. */
export type AgentV2LinkRenderer = (href: string, children: ReactNode) => ReactNode

type AgentV2MarkdownProps = {
  renderLink?: AgentV2LinkRenderer
  text: string
  referencesByCitationId?: Record<string, AgentV2Reference>
  /** Citation id whose source is open in the content viewer — its badge gets the active background. */
  activeCitationId?: string | null
  onCitationClick?: (citationId: string) => void
  onFileLinkClick?: (href: string, title: string) => boolean
  onCaseStatusClick?: (citationId: string) => void
  /** Presence enables inline treatment flags; the flag routes here, not onCaseStatusClick. */
  onTreatmentFlagClick?: (citationId: string) => void
  /** Wrap resolved citations in the hover card (all reference types). Chat sets this; the artifact edit review doesn't. */
  withCitationHoverCard?: boolean
  /**
   * Whether onCitationClick opens sources in the in-app content viewer (useAgentV2ReferenceOpensInPanel)
   * rather than a new browser tab. A source only the panel can show (a docket) renders unavailable without it.
   */
  referencesOpenInPanel?: boolean
  isStreaming?: boolean
}

/**
 * Renders chat markdown, owning the shared transformation and citation behavior.
 * Callers own surface-specific layout and typography.
 *
 * Citations reach the renderer as anchors: replaceAgentV2CitationTokens rewrites each
 * citation token to a link whose href carries the citation id and form (numbered badge
 * vs. legal-citation text), and the `a` component override decodes that href and picks
 * the citation's state — plain prose while the message is still streaming, greyed
 * unverified/unavailable spans once settled, or a clickable trigger (badge button /
 * InlineTextButton) when the reference is resolved. Anchors without a citation href
 * render as ordinary links.
 *
 * Resolved triggers get the citation hover card only where withCitationHoverCard is
 * set (chat); the other consumer (the artifact edit review's read-only diff) renders
 * bare triggers. The artifact editor itself is ProseMirror, not this component.
 */
export function AgentV2Markdown({
  activeCitationId,
  onCitationClick,
  onFileLinkClick,
  renderLink,
  onCaseStatusClick,
  onTreatmentFlagClick,
  referencesByCitationId,
  text,
  withCitationHoverCard,
  referencesOpenInPanel = false,
  isStreaming,
}: AgentV2MarkdownProps) {
  const labeledCitations = useMemo(() => labelAgentV2Citations(text, referencesByCitationId), [text, referencesByCitationId])
  const markdownText = useMemo(() => replaceAgentV2CitationTokens(text, labeledCitations), [text, labeledCitations])

  const resolvedCitationIds = useMemo(
    () => new Set(labeledCitations.filter((citation) => citation.isResolved).map((citation) => citation.citationId)),
    [labeledCitations]
  )

  // First occurrence wins: a repeated citation id keeps its first label ("1", not "1.3")
  // because that is the label on its chip in the sources list — the hover-card header
  // exists to map the trigger to that list.
  const labelsByCitationId = useMemo(() => {
    const labels = new Map<string, string | null>()
    for (const citation of labeledCitations) {
      if (!labels.has(citation.citationId)) labels.set(citation.citationId, citation.label)
    }
    return labels
  }, [labeledCitations])

  // A citation is unresolvable when its stored reference cannot open on this surface: no
  // viewer session (missing storage path, unsupported render format), or a source only the
  // in-app panel can show where sources open in a new tab. Clicking it would be a silent
  // no-op. Only this message's citations are checked, not the whole conversation map.
  const unresolvableCitationIds = useMemo(() => {
    const ids = new Set<string>()
    for (const citationId of resolvedCitationIds) {
      const reference = referencesByCitationId?.[citationId]
      if (reference && !canOpenAgentV2Reference(reference, { opensInPanel: referencesOpenInPanel })) ids.add(citationId)
    }
    return ids
  }, [resolvedCitationIds, referencesByCitationId, referencesOpenInPanel])

  const reportedUnresolvableCitationIdsRef = useRef(new Set<string>())
  useEffect(() => {
    for (const citationId of resolvedCitationIds) {
      if (!unresolvableCitationIds.has(citationId)) continue
      if (reportedUnresolvableCitationIdsRef.current.has(citationId)) continue
      const reference = referencesByCitationId?.[citationId]
      // Every inactive agency citation is intentional: the reference has no stored PDF original to
      // open in-app and no verified HTML publisher page to link to. Reporting them would bury real failures.
      if (reference?.source_type === 'administrative_decision') continue
      // Only a reference no surface can open is broken; one this surface cannot show is expected.
      if (reference && canOpenAgentV2Reference(reference, { opensInPanel: true })) continue
      reportedUnresolvableCitationIdsRef.current.add(citationId)
      Sentry.captureException(new Error('Agent v2 citation reference failed to resolve to a viewer session'), {
        extra: {
          citationId,
          referenceId: reference?.reference_id,
          sourceType: reference?.source_type,
        },
      })
    }
  }, [resolvedCitationIds, referencesByCitationId, unresolvableCitationIds])

  // Inline-flag impressions are container-owned and authority-denominated: one event
  // per warning-status authority per message once it settles, no matter how many
  // mentions carry a flag — mirroring the canvas host's per-authority semantics so the
  // flag→citator engagement ratio compares across surfaces. Streamed renders fire
  // nothing (the guard set survives them), so remounts can't inflate the metric.
  const { trackEvent } = useAnalytics()
  const firedFlagImpressionsRef = useRef(new Set<string>())
  useEffect(() => {
    if (isStreaming || !onTreatmentFlagClick) return
    const flaggedCitationIds = new Set(
      labeledCitations.filter((citation) => citation.authorityText !== null && citation.isResolved).map((citation) => citation.citationId)
    )
    for (const citationId of flaggedCitationIds) {
      if (unresolvableCitationIds.has(citationId)) continue
      const reference = referencesByCitationId?.[citationId]
      const caseStatus = reference?.source_type === 'caselaw' ? reference.case_status : null
      if (!isWarningCaseStatus(caseStatus)) continue
      // Keyed by caseStatus to match the canvas twin (inline-treatment-flag-decorations)
      // — the two dedups must stay authority-denominated the same way.
      const impressionKey = `${citationId}:${caseStatus}`
      if (firedFlagImpressionsRef.current.has(impressionKey)) continue
      firedFlagImpressionsRef.current.add(impressionKey)
      trackCitatorBadgeImpression({ trackEvent, surface: CitatorSurface.ChatInline, badgeType: mapCaseStatusToBadgeType(caseStatus) })
    }
  }, [isStreaming, onTreatmentFlagClick, labeledCitations, unresolvableCitationIds, referencesByCitationId, trackEvent])

  const linkContext = {
    activeCitationId,
    isStreaming,
    labelsByCitationId,
    onCaseStatusClick,
    onCitationClick,
    onFileLinkClick,
    renderLink,
    onTreatmentFlagClick,
    referencesByCitationId,
    resolvedCitationIds,
    unresolvableCitationIds,
    withCitationHoverCard,
  }

  return (
    <MarkdownLinkContext.Provider value={linkContext}>
      <Markdown
        className="agent-v2-markdown"
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          rehypeRaw,
          [rehypeWrap, { selector: 'table', wrapper: 'div.source-html-table-wrapper' }],
          [
            rehypeTagNameChange,
            [
              { tagName: 'pre', newTagName: 'div' },
              { tagName: 'code', newTagName: 'div' },
            ],
          ],
        ]}
        components={components}
      >
        {markdownText}
      </Markdown>
    </MarkdownLinkContext.Provider>
  )
}

type MarkdownLinkContextValue = Pick<
  AgentV2MarkdownProps,
  | 'activeCitationId'
  | 'isStreaming'
  | 'onCaseStatusClick'
  | 'onCitationClick'
  | 'onFileLinkClick'
  | 'renderLink'
  | 'onTreatmentFlagClick'
  | 'referencesByCitationId'
  | 'withCitationHoverCard'
> & {
  labelsByCitationId: Map<string, string | null>
  resolvedCitationIds: Set<string>
  unresolvableCitationIds: Set<string>
}

const MarkdownLinkContext = createContext<MarkdownLinkContextValue>({
  labelsByCitationId: new Map(),
  resolvedCitationIds: new Set(),
  unresolvableCitationIds: new Set(),
})

// Streaming changes context values, never the component types that own card state.
const components: Components = {
  div: ({ children, ...divProps }) => {
    const filteredChildren = Array.isArray(children)
      ? children.map((child) => (typeof child === 'string' ? child.replace(/\n{2,}/g, '\n\n') : child))
      : children

    return <div {...divProps}>{filteredChildren}</div>
  },
  li: ({ children, ...liProps }) => {
    const filteredChildren = Array.isArray(children) ? children.filter((child) => child !== '\n') : children

    return <li {...liProps}>{filteredChildren}</li>
  },
  a: MarkdownAnchor,
}

function MarkdownAnchor({ children, href }: ComponentPropsWithoutRef<'a'>) {
  const {
    activeCitationId,
    isStreaming,
    labelsByCitationId,
    onCaseStatusClick,
    onCitationClick,
    onFileLinkClick,
    renderLink,
    onTreatmentFlagClick,
    referencesByCitationId,
    resolvedCitationIds,
    unresolvableCitationIds,
    withCitationHoverCard,
  } = useContext(MarkdownLinkContext)

  const citation = readCitationHref(href)
  if (!citation) {
    const customLink = href ? renderLink?.(href, children) : null
    if (customLink != null) return <>{customLink}</>
    return (
      <a
        href={href}
        onClick={(event) => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
          if (href && onFileLinkClick?.(href, event.currentTarget.textContent ?? 'Source')) event.preventDefault()
        }}
      >
        {children}
      </a>
    )
  }

  const { citationId, isLegalCitationLink } = citation

  // Unresolved (no reference in the conversation map): while the message is
  // still streaming the reference usually arrives moments later, so authority
  // text renders as plain prose (it is part of the sentence) and a badge —
  // pure metadata — renders nothing yet. Once the stream settles, both render
  // grey but never hidden.
  if (!resolvedCitationIds.has(citationId)) {
    if (isStreaming) {
      return isLegalCitationLink ? <span>{children}</span> : null
    }
    return isLegalCitationLink ? (
      <span title={kAgentV2UnverifiedSourceLabel} className="text-brand-neutral-600 underline decoration-brand-neutral-600/40 underline-offset-2">
        {children}
      </span>
    ) : (
      <span title={kAgentV2UnverifiedSourceLabel} aria-label={kAgentV2UnverifiedSourceLabel} className="text-brand-neutral-600 px-1">
        {children}
      </span>
    )
  }
  const reference = referencesByCitationId?.[citationId]
  if (unresolvableCitationIds.has(citationId)) {
    if (reference?.source_type === 'administrative_decision' && withCitationHoverCard) {
      return (
        <AgentV2UnavailableSource sourceUrl={reference.source.source_url} citationId={citationId}>
          {isLegalCitationLink ? children : undefined}
        </AgentV2UnavailableSource>
      )
    }
    return isLegalCitationLink ? (
      <span
        aria-disabled="true"
        title={kAgentV2SourceUnavailableLabel}
        className="text-brand-neutral-600 underline decoration-brand-neutral-600/40 underline-offset-2 cursor-not-allowed"
      >
        {children}
      </span>
    ) : (
      <span aria-disabled="true" title={kAgentV2SourceUnavailableLabel} className="text-brand-neutral-600 px-1 cursor-not-allowed">
        {children}
      </span>
    )
  }

  const externalUrl = reference ? getAgentV2ExternalReferenceUrl(reference) : null
  if (reference && externalUrl && withCitationHoverCard) {
    // No footnote number, per the design; data-citation-id keeps it in copied text.
    // nowrap keeps icon + domain + external icon together; authority text still wraps.
    return (
      <AgentV2WebSourceTooltip
        source={{ url: externalUrl, title: getAgentV2ReferenceDisplayName(reference) }}
        supports={reference.source_type === 'web' ? [{ reference, chipLabel: labelsByCitationId.get(citationId) ?? '' }] : undefined}
      >
        <Link href={externalUrl} external data-citation-id={citationId} className="whitespace-nowrap" externalIconClassName="!align-text-bottom !translate-y-0">
          {isLegalCitationLink && <span className="whitespace-normal">{children} </span>}
          <AgentV2WebSourceIcon url={externalUrl} className="!mr-1 h-4 w-4 !align-text-bottom !translate-y-0" />
          {new URL(externalUrl).hostname.replace(/^www\./, '')}
        </Link>
      </AgentV2WebSourceTooltip>
    )
  }

  const isActive = citationId === activeCitationId
  const trigger =
    reference?.source_type === 'administrative_decision' && externalUrl ? (
      <Link
        href={externalUrl}
        external
        data-citation-id={isLegalCitationLink ? citationId : undefined}
        className={isLegalCitationLink ? undefined : 'agent-v2-citation-marker whitespace-nowrap'}
      >
        <AgentV2WebSourceIcon url={externalUrl} className="mr-1 h-4 w-4" />
        {children}
      </Link>
    ) : isLegalCitationLink ? (
      <InlineTextButton
        className={isActive ? 'bg-brand-neutral-200 rounded-md' : undefined}
        // Lets copy-agent-v2-response append the footnote number after the
        // authority text, since this trigger renders prose rather than a badge.
        data-citation-id={citationId}
        onClick={() => onCitationClick?.(citationId)}
      >
        {children}
      </InlineTextButton>
    ) : (
      <button
        className={`agent-v2-citation-marker text-brand-700 px-1 hover:text-brand-800${isActive ? ' bg-brand-neutral-200 rounded-md' : ''}`}
        onClick={() => onCitationClick?.(citationId)}
        type="button"
      >
        {children}
      </button>
    )

  const caseStatus = reference?.source_type === 'caselaw' ? reference.case_status : null
  const treatmentFlag =
    isLegalCitationLink && onTreatmentFlagClick && isWarningCaseStatus(caseStatus) ? (
      <InlineTreatmentFlag
        className="ml-1"
        caseStatus={caseStatus}
        surface={CitatorSurface.ChatInline}
        onViewCitationAnalysis={() => onTreatmentFlagClick(citationId)}
      />
    ) : null

  if (!reference || !withCitationHoverCard)
    return (
      <>
        {trigger}
        {treatmentFlag}
      </>
    )

  return (
    <>
      <AgentV2CitationTooltip
        reference={reference}
        onViewReference={() => onCitationClick?.(citationId)}
        onViewCitationAnalysis={() => onCaseStatusClick?.(citationId)}
      >
        {trigger}
      </AgentV2CitationTooltip>
      {treatmentFlag}
    </>
  )
}

function readCitationHref(href: string | undefined): { citationId: string; isLegalCitationLink: boolean } | null {
  if (href?.startsWith(kAgentV2LegalCitationHrefPrefix)) {
    return { citationId: decodeURIComponent(href.slice(kAgentV2LegalCitationHrefPrefix.length)), isLegalCitationLink: true }
  }
  if (href?.startsWith(kAgentV2CitationHrefPrefix)) {
    return { citationId: decodeURIComponent(href.slice(kAgentV2CitationHrefPrefix.length)), isLegalCitationLink: false }
  }
  return null
}
